/**
 * Budget Forecast Chart Component
 * Displays budget forecast visualization
 */
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { TrendingUp } from 'lucide-react'
import { formatCurrency } from '../../utils/apiTransformers'
import { BudgetForecast } from '../../services/programBudgetForecast'

interface BudgetForecastChartProps {
  forecast: BudgetForecast | null
  loading?: boolean
}

export default function BudgetForecastChart({ forecast, loading }: BudgetForecastChartProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-[#f0f4f8] rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-[#f0f4f8] rounded"></div>
        </div>
      </div>
    )
  }

  if (!forecast || !forecast.forecasted_spending.length) {
    return (
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
        <h3 className="text-xl font-semibold text-[#102a43] mb-4">Budget Forecast</h3>
        <p className="text-[#829ab1]">No forecast data available</p>
      </div>
    )
  }

  const chartData = forecast.forecasted_spending.map((item, index) => ({
    month: item.month,
    forecasted: item.forecasted_amount,
    cumulative: forecast.forecasted_spending.slice(0, index + 1).reduce((sum, i) => sum + i.forecasted_amount, 0)
  }))

  return (
    <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-[#102a43] mb-1">Budget Forecast</h3>
          <p className="text-sm text-[#829ab1]">Next {forecast.forecast_months} months projection</p>
        </div>
        <div className="flex items-center gap-2 text-[#486581]">
          <TrendingUp className="w-5 h-5" />
          <span className="text-sm font-medium">
            {formatCurrency(forecast.total_forecasted)}
          </span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis 
            dataKey="month" 
            stroke="#829ab1"
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            stroke="#829ab1"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#fff', 
              border: '1px solid #bcccdc',
              borderRadius: '8px'
            }}
            formatter={(value: number) => formatCurrency(value)}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="forecasted" 
            stroke="#486581" 
            strokeWidth={2}
            name="Monthly Forecast"
            dot={{ fill: '#486581', r: 4 }}
          />
          <Line 
            type="monotone" 
            dataKey="cumulative" 
            stroke="#10b981" 
            strokeWidth={2}
            name="Cumulative"
            dot={{ fill: '#10b981', r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-4 flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#486581]"></div>
          <span className="text-[#829ab1]">Monthly Forecast</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#10b981]"></div>
          <span className="text-[#829ab1]">Cumulative Total</span>
        </div>
        <div className="ml-auto text-[#829ab1]">
          Confidence: <span className="font-medium text-[#102a43] capitalize">{forecast.confidence}</span>
        </div>
      </div>
    </div>
  )
}

